<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { navItems } from '~/data/pages/banks/mocks'
import {
  WidgetBankPageAboutBankHeader,
  WidgetBankPageRatesChart,
  WidgetBankPageRatesChartNbkr,
  WidgetBankPageRatesWrapper,
  WidgetBankPageRatesConverter
} from '~/components/widget/bank-page'
import type { IBank } from '~/components/entity/bank'
import type { IResponseData } from '~/types'
import { WidgetSidebarRates } from '~/components/widget/sidebar-rates'
import {
  WidgetOrganizationAsideContacts,
  mapBankToOrganizationInfo
} from '~/components/widget/organization-aside-contacts'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import { WidgetRatesNbkr } from '~/components/widget/rates'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetRateAlertSidebar } from '~/components/widget/rate-alert-sidebar'
import type { IOrganizationRateData } from '~/components/feature/rate-alert'

const route = useRoute()
const bankUrl = `/banks/${route.params.bank}`
const { data } = await useApiWith404<IResponseData<IBank>>(() => useUrlWithParams(bankUrl), {
  key: `banks-rates-${route.params.bank}`
})
const bank = computed(() => data.value?.data)

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: bank.value?.title
  }
}))

const { data: ratesData, status: ratesStatus } = await useFetch(
  '/api/rates/bank-cash-cashless-cached',
  {
    params: { bankId: bank.value?.id },
    // The bank request may have failed or been cancelled by a quick navigation
    immediate: !!bank.value?.id
  }
)

const hasRates = computed(() => {
  if (ratesStatus.value === 'pending') return true
  const regular = ratesData.value?.currentRegularRates ?? {}
  const cashless = ratesData.value?.currentCashlessRates ?? {}
  return Object.keys(regular).length > 0 || Object.keys(cashless).length > 0
})

const { t, locale } = useI18n()

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
  { title: bank.value?.title || t('common.bank'), url: `/bank/${route.params.bank}` },
  { title: t('common.exchange_rates'), url: `/bank/${route.params.bank}/rates` }
])
const organizationInfo = computed(() => mapBankToOrganizationInfo(bank.value))

const { data: organizationsRatesData } = await useFetch('/api/rates/organizations-cached', {
  query: {
    lang: locale.value || 'ru'
  }
})

const allOrganizationsRates = computed<IOrganizationRateData[]>(() => {
  return (
    organizationsRatesData.value?.data.map((item) => ({
      id: item.id,
      title: item.title,
      rates: item.rates
    })) || []
  )
})

const ratesUpdatedAt = computed<string | undefined>(() => {
  const orgs = organizationsRatesData.value?.data
  if (!orgs || !bank.value?.id) return undefined
  return orgs.find((item: { id: number; created_at: string }) => item.id === bank.value!.id)
    ?.created_at
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="organizationInfo"
              :organization="organizationInfo"
            />
            <WidgetRateAlertSidebar
              v-if="bank && !bank.is_nbkr"
              :bank-id="bank.id"
              :bank-name="bank.title"
              :all-organizations-rates="allOrganizationsRates"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <SidebarBlock variant="white" :heading-text="$t('common.exchange_rates')">
              <WidgetSidebarRates />
            </SidebarBlock>
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetBankPageAboutBankHeader :bank="bank">
            <WidgetBankServicesMenu
              v-if="bank && bank.status === 'active'"
              :organization="bank"
              :nav-items="navItems"
            />
          </WidgetBankPageAboutBankHeader>
          <WidgetSeoContent
            heading-level="h2"
            heading-class="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: bank?.official_title ?? ''
            }"
          />
          <ContentBlock v-if="bank && !bank.is_nbkr && hasRates" variant="white">
            <WidgetBankPageRatesWrapper
              :bank-id="bank.id"
              :bank-name="bank.official_title"
              :bank-url="bank.url"
              :rates-data="ratesData"
              :rates-status="ratesStatus"
              :updated-at="ratesUpdatedAt"
            />
          </ContentBlock>

          <ContentBlock
            v-if="bank && !bank.is_nbkr && hasRates"
            :heading-text="$t('common.currency_converter')"
            variant="white"
          >
            <WidgetBankPageRatesConverter
              :bank-id="bank.id"
              :bank-name="bank.official_title"
              :rates-data="ratesData"
              :rates-status="ratesStatus"
            />
          </ContentBlock>

          <ContentBlock
            v-if="bank && bank.is_nbkr"
            :heading-text="$t('page.bank.rates.nbkr_heading')"
            variant="white"
            class="mt-6"
          >
            <WidgetRatesNbkr />
          </ContentBlock>
          <ContentBlock v-if="bank" variant="white">
            <WidgetBankPageRatesChartNbkr v-if="bank.is_nbkr" :id="bank.id" :title="bank.title" />
            <WidgetBankPageRatesChart v-else :id="bank.id" :title="bank.title" />
          </ContentBlock>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
